import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Pencil } from 'lucide-react';
import { useClients, useCreateClient, useUpdateClient } from '../hooks/useClients.js';
import PageHeader from '../components/PageHeader.jsx';
import Table from '../components/Table.jsx';
import Badge from '../components/Badge.jsx';
import Button from '../components/Button.jsx';
import Modal from '../components/Modal.jsx';
import Input from '../components/Input.jsx';
import { PROVINCIAS } from '../constants/provincias.js';

const STATUS_VARIANT = { active: 'success', suspended: 'warning', disabled: 'danger' };

function ProvinciaSelect({ value, onChange }) {
  return (
    <Input as="select" label="Provincia" value={value} onChange={onChange}>
      <option value="">Sin definir</option>
      {PROVINCIAS.map((p) => <option key={p} value={p}>{p}</option>)}
    </Input>
  );
}

function CreateClientModal({ open, onClose }) {
  const createClient = useCreateClient();
  const [form, setForm] = useState({ code: '', businessName: '', cuit: '', email: '', province: '' });
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await createClient.mutateAsync({ ...form, province: form.province || undefined });
      onClose();
      setForm({ code: '', businessName: '', cuit: '', email: '', province: '' });
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear el cliente');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Nuevo cliente">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Input label="Código" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
        <Input label="Razón social" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} required />
        <Input label="CUIT (11 dígitos)" value={form.cuit} onChange={(e) => setForm({ ...form, cuit: e.target.value })} required pattern="\d{11}" />
        <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <ProvinciaSelect value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} />
        <p className="text-xs text-text-muted -mt-2">Se usa como ST/L en el CSR de certificados de este cliente.</p>
        {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button type="submit" loading={createClient.isPending}>Crear</Button>
        </div>
      </form>
    </Modal>
  );
}

function EditClientModal({ client, onClose }) {
  const updateClient = useUpdateClient();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!client) return;
    setForm({
      businessName: client.business_name,
      email: client.email || '',
      phone: client.phone || '',
      status: client.status,
      defaultEnvironment: client.default_environment,
      province: client.province || '',
    });
  }, [client?.id]);

  const handleClose = () => {
    setForm(null);
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await updateClient.mutateAsync({ id: client.id, ...form, province: form.province || undefined });
      handleClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al editar el cliente');
    }
  };

  return (
    <Modal open={!!client} onClose={handleClose} title={`Editar ${client?.code || ''}`}>
      {form && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <Input label="Razón social" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} required />
          <Input label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Input label="Teléfono" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <ProvinciaSelect value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} />
          <Input as="select" label="Entorno default" value={form.defaultEnvironment} onChange={(e) => setForm({ ...form, defaultEnvironment: e.target.value })}>
            <option value="testing">Testing (homologación)</option>
            <option value="production">Producción</option>
          </Input>
          <Input as="select" label="Estado" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Activo</option>
            <option value="suspended">Suspendido</option>
            <option value="disabled">Deshabilitado</option>
          </Input>
          {error && <p className="text-sm text-red-400">{typeof error === 'string' ? error : JSON.stringify(error)}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={handleClose}>Cancelar</Button>
            <Button type="submit" loading={updateClient.isPending}>Guardar</Button>
          </div>
        </form>
      )}
    </Modal>
  );
}

export default function Clients() {
  const { data, isLoading } = useClients();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle="Tenants que operan contra ARCA a través de arca-api"
        actions={<Button onClick={() => setCreateOpen(true)}><Plus size={16} /> Nuevo cliente</Button>}
      />
      <div className="card">
        <Table
          loading={isLoading}
          data={data}
          emptyMessage="Todavía no hay clientes"
          columns={[
            { key: 'code', label: 'Código' },
            { key: 'business_name', label: 'Razón social' },
            { key: 'cuit', label: 'CUIT' },
            { key: 'default_environment', label: 'Entorno default' },
            { key: 'status', label: 'Estado', render: (row) => <Badge variant={STATUS_VARIANT[row.status]}>{row.status}</Badge> },
            {
              key: 'actions', label: '', render: (row) => (
                <div className="flex items-center gap-1 justify-end">
                  <button onClick={() => navigate(`/clients/${row.id}`)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-accent" title="Ver detalle">
                    <Eye size={15} />
                  </button>
                  <button onClick={() => setEditingClient(row)} className="p-1.5 rounded-lg hover:bg-bg-elevated text-text-muted hover:text-accent" title="Editar">
                    <Pencil size={15} />
                  </button>
                </div>
              ),
            },
          ]}
          rowKey="id"
        />
      </div>
      <CreateClientModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditClientModal client={editingClient} onClose={() => setEditingClient(null)} />
    </div>
  );
}
